import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { usePageBack } from "@/contexts/PageBackContext";
import { LG_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  onBack?: () => void;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  meta,
  actions,
  onBack,
}: PageHeaderProps): JSX.Element {
  const { t } = useTranslation();

  // Publish to the app header, which shows a back arrow in place of the
  // hamburger on mobile. The in-body button below stays for desktop, where
  // there is room for it and the header has no hamburger to replace.
  // False on auth/onboarding routes, which mount outside AppLayout and so
  // have no provider and no app header to show the arrow — there the in-body
  // button must stay, mobile included.
  const shownInHeader = usePageBack(onBack);
  const isDesktop = useMediaQuery(LG_QUERY);

  return (
    <div className="fx-phead">
      <div className="fx-phead-text">
        {eyebrow ? <span className="fx-eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {description ? <div className="sub">{description}</div> : null}
        {meta ? <div className="meta">{meta}</div> : null}
      </div>
      {onBack || actions ? (
        <div className="fx-phead-actions">
          {onBack && (isDesktop || !shownInHeader) && (
            <button
              type="button"
              className="fx-btn fx-btn-ghost"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("pages.common.back")}
            </button>
          )}
          {actions}
        </div>
      ) : null}
    </div>
  );
}
